<script setup lang="ts">
import { onMounted, watch, computed } from 'vue'
import { RouterView } from 'vue-router'
import { useAuthStore } from '@/stores/auth.store'
import { useUiStore } from '@/stores/ui.store'
import { useConnectionStore } from '@/stores/connection.store'
import { useWorkspaceStore } from '@/stores/workspace.store'
import { applyPrimaryRamp } from '@/utils/tenantTheme'
import { getPublicSite } from '@/api/site.api'
import AppToast from '@/components/common/AppToast.vue'
import ConnectionStatus from '@/components/common/ConnectionStatus.vue'

const authStore = useAuthStore()
const uiStore = useUiStore()
const connectionStore = useConnectionStore()
const workspaceStore = useWorkspaceStore()

// Tenant-brand the app chrome: a client context (their team logged in, or a
// Mavriq user acting-as that client) recolors the primary ramp to the client's
// brand; Mavriq's own/global view stays gold. Sourced from the public brand
// endpoint (returns the tenant's branding_primary_color).
const activeTenantId = computed(() => {
  const clientCo = (authStore.user as any)?.company_id
  if (authStore.isClientUser && clientCo) return String(clientCo)
  return workspaceStore.currentCompanyId || ''
})
let themedFor = ' '
async function applyTenantTheme(id: string) {
  if (id === themedFor) return
  themedFor = id
  if (!id) { applyPrimaryRamp(null); return }
  try {
    const site = await getPublicSite(id)
    applyPrimaryRamp(site?.brand?.primary || null)
  } catch { applyPrimaryRamp(null) }
}
watch(activeTenantId, (id) => { applyTenantTheme(id) }, { immediate: true })

// Initialize auth state on mount
onMounted(() => {
  authStore.initAuthListener()
})

// Watch for theme changes
watch(
  () => uiStore.isDarkMode,
  (isDark) => {
    if (isDark) {
      document.documentElement.classList.add('dark')
    } else {
      document.documentElement.classList.remove('dark')
    }
  },
  { immediate: true }
)
</script>

<template>
  <div class="min-h-screen bg-gray-50 dark:bg-gray-900">
    <!-- Connection Status Banner (shows during retries) -->
    <ConnectionStatus />

    <!-- Full-page loading screen during initial load or server cold start -->
    <div
      v-if="authStore.isLoading || connectionStore.isConnecting"
      class="fixed inset-0 z-50 flex flex-col items-center justify-center bg-gray-50 dark:bg-gray-900"
    >
      <!-- Mavriq Logo -->
      <img
        src="/Mavriq-Icon-Color.svg"
        alt="Mavriq"
        class="h-20 w-20 mb-6"
      />

      <!-- Spinner -->
      <svg
        class="h-8 w-8 animate-spin text-primary-600 mb-4"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
      >
        <circle
          class="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          stroke-width="4"
        />
        <path
          class="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        />
      </svg>

      <!-- Message -->
      <p class="text-lg font-medium text-gray-700 dark:text-gray-300">
        {{ connectionStore.connectionMessage || 'Loading...' }}
      </p>

      <!-- Retry indicator -->
      <p
        v-if="connectionStore.retryCount > 0"
        class="mt-2 text-sm text-gray-500 dark:text-gray-400"
      >
        Attempt {{ connectionStore.retryCount }} of {{ connectionStore.maxRetries }}
      </p>

      <!-- Hint (only show during server cold start) -->
      <p
        v-if="connectionStore.isConnecting"
        class="mt-4 text-xs text-gray-400 dark:text-gray-500 max-w-xs text-center"
      >
        The server is waking up. This usually takes a few seconds.
      </p>
    </div>

    <RouterView />
    <AppToast />
  </div>
</template>
